// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import * as React from 'react';
import { useRoomContext } from '@livekit/components-react';
import { LogLevel, RemoteTrackPublication, setLogLevel } from 'livekit-client'; // @ts-ignore
import { tinykeys } from 'tinykeys';

import styles from '@/styles/tkit/Debug.module.css';

export const useDebugMode = ({ logLevel }: { logLevel?: LogLevel }) => {
  const room = useRoomContext();

  React.useEffect(() => {
    setLogLevel(logLevel ?? 'debug');

    // @ts-expect-error
    window.__lk_room = room;

    return () => {
      // @ts-expect-error
      window.__lk_room = undefined;
    };
  }, [room, logLevel]);
};

export const DebugMode = ({ logLevel }: { logLevel?: LogLevel }) => {
  const room = useRoomContext();
  const [isOpen, setIsOpen] = React.useState(false);
  const [, setRender] = React.useState({});
  const [roomSid, setRoomSid] = React.useState('');
  const lp = room.localParticipant;
  const [fpsMap, setFpsMap] = React.useState<Record<string, number>>({});
  const [remoteDimsMap, setRemoteDimsMap] = React.useState<
    Record<string, { w: number; h: number }>
  >({});

  React.useEffect(() => {
    room.getSid().then(setRoomSid);
  }, [room]);

  useDebugMode({ logLevel });

  React.useEffect(() => {
    if (window) {
      const unsubscribe = tinykeys(window, {
        'Shift+D': () => {
          setIsOpen((open) => !open);
        },
      });

      // timer to re-render
      const interval = setInterval(() => {
        setRender({});
      }, 1000);

      return () => {
        unsubscribe();
        clearInterval(interval);
      };
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;

    const updateStats = async () => {
      const entries: Record<string, number> = {};
      for (const pub of lp.trackPublications.values()) {
        if (pub.kind !== 'video' || !pub.track) continue;
        const fps = await computeTrackFPS(pub.track, pub.trackSid);
        if (fps != null) entries[pub.trackSid] = fps;
      }
      setFpsMap(entries);
    };

    const interval = setInterval(updateStats, 1000);
    return () => clearInterval(interval);
  }, [lp, isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;

    const updateRemoteDims = async () => {
      const newMap: Record<string, { w: number; h: number }> = {};

      for (const p of room.remoteParticipants.values()) {
        for (const pub of p.trackPublications.values()) {
          if (pub.kind !== 'video' || !pub.isSubscribed || !pub.track) continue;

          // Получаем реальные размеры из WebRTC Stats
          const dims = await getRealVideoDimensions(pub.track);
          if (dims) {
            newMap[pub.trackSid] = { w: dims.width, h: dims.height };
          }
        }
      }
      setRemoteDimsMap(newMap);
    };

    const interval = setInterval(updateRemoteDims, 1000); // Обновляем раз в секунду
    return () => clearInterval(interval);
  }, [room, isOpen]);

  if (typeof window === 'undefined' || !isOpen) {
    return null;
  }

  async function getRealVideoDimensions(
    track: any // Local or Remote Track
  ): Promise<{ width: number; height: number } | null> {
    if (!track?.getRTCStatsReport) return null;

    try {
      const report = await track.getRTCStatsReport();

      // Ищем именно inbound-rtp (входящие данные), если это remote participant
      // Или outbound-rtp, если это local participant (для проверки публикации)
      for (const stats of report.values()) {
        if (stats.type === 'inbound-rtp') {
          // frameWidth/frameHeight показывают разрешение текущего получаемого кадра
          const w = stats.frameWidth;
          const h = stats.frameHeight;

          if (w && h) {
            return { width: w, height: h };
          }
        }
      }
    } catch (err) {
      console.warn('Stats error', err);
    }
    return null;
  }

  const handleSimulate = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (value == '') {
      return;
    }
    event.target.value = '';
    let isReconnect = false;
    switch (value) {
      case 'signal-reconnect':
        isReconnect = true;

      // fall through
      default:
        // @ts-expect-error
        room.simulateScenario(value);
    }
  };

  if (!isOpen) {
    return <></>;
  } else {
    return (
      <div className={styles.overlay}>
        {/*<section id="room-info">*/}
        {/*  <h3>*/}
        {/*    Room Info {room.name}: {roomSid}*/}
        {/*  </h3>*/}
        {/*</section>*/}
        <details open>
          <summary>
            <b>Мои</b>
            {/*<b>Мой: {lp.identity}</b>*/}
          </summary>
          <details open className={styles.detailsSection}>
            <summary>
              <b>Транслируются</b>
            </summary>
            <div>
              {Array.from(lp.trackPublications.values()).map((t, index) => (
                <>
                  <div>
                    <i>
                      {t.source.toString()}
                      {/*&nbsp;<span>{t.trackSid}</span>*/}
                      <span>_{index}</span>
                    </i>
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <td>Тип</td>
                        <td>
                          {t.kind}&nbsp;
                          {t.kind === 'video' && (
                            <span>
                              {t.track?.dimensions?.width}x{t.track?.dimensions?.height}
                            </span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Битрейт</td>
                        <td>{Math.ceil(t.track!.currentBitrate / 1000)} кб/c</td>
                      </tr>
                      {t.kind === 'video' && (
                        <tr>
                          <td>FPS</td>
                          <td>{fpsMap[t.trackSid] ?? '—'}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              ))}
            </div>
          </details>
          {/*<details open className={styles.detailsSection}>*/}
          {/*  <summary>*/}
          {/*    <b>Permissions</b>*/}
          {/*  </summary>*/}
          {/*  <div>*/}
          {/*    <table>*/}
          {/*      <tbody>*/}
          {/*        {lp.permissions &&*/}
          {/*          Object.entries(lp.permissions).map(([key, val]) => (*/}
          {/*            <>*/}
          {/*              <tr>*/}
          {/*                <td>{key}</td>*/}
          {/*                {key !== 'canPublishSources' ? (*/}
          {/*                  <td>{val.toString()}</td>*/}
          {/*                ) : (*/}
          {/*                  <td> {val.join(', ')} </td>*/}
          {/*                )}*/}
          {/*              </tr>*/}
          {/*            </>*/}
          {/*          ))}*/}
          {/*      </tbody>*/}
          {/*    </table>*/}
          {/*  </div>*/}
          {/*</details>*/}
        </details>

        <details>
          <summary>
            <b>Принимаются</b>
          </summary>
          {Array.from(room.remoteParticipants.values()).map((p) => (
            <details key={p.sid} className={styles.detailsSection}>
              <summary>
                <b>
                  {p.identity.split('_')[0]}
                  <span></span>
                </b>
              </summary>
              <div>
                {Array.from(p.trackPublications.values()).map((t, index) => (
                  <>
                    <div>
                      <i>
                        {t.source.toString()}
                        <span>_{index}</span>
                        {/*&nbsp;<span>{t.trackSid}</span>*/}
                      </i>
                    </div>
                    <table>
                      <tbody>
                        <tr>
                          <td>Тип</td>
                          <td>
                            {t.kind}&nbsp;
                            {/*{t.kind === 'video' && (*/}
                            {/*  <span>*/}
                            {/*    {t.dimensions?.width}x{t.dimensions?.height}*/}
                            {/*  </span>*/}
                            {/*)}*/}
                            {t.kind === 'video' && (
                              <span>
                                {/* Показываем реальные размеры из stats, если они есть, иначе fallback на dimensions */}
                                {remoteDimsMap[t.trackSid]?.w ?? t.dimensions?.width} x{' '}
                                {remoteDimsMap[t.trackSid]?.h ?? t.dimensions?.height}
                                {/* Для наглядности можно подсветить красным, если реальный размер меньше заявленного */}
                                {remoteDimsMap[t.trackSid] &&
                                  remoteDimsMap[t.trackSid].h < (t.dimensions?.height ?? 0) && (
                                    <span style={{ color: 'red' }}> (Degraded)</span>
                                  )}
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Статус</td>
                          <td>{trackStatus(t)}</td>
                        </tr>
                        {t.track && (
                          <tr>
                            <td>Битрейт</td>
                            <td>{Math.ceil(t.track.currentBitrate / 1000)} кб/c</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </>
                ))}
              </div>
            </details>
          ))}
        </details>
      </div>
    );
  }
};

function trackStatus(t: RemoteTrackPublication): string {
  if (t.isSubscribed) {
    return t.isEnabled ? 'enabled' : 'disabled';
  } else {
    return 'unsubscribed';
  }
}

type FPSCacheEntry = {
  lastFrames: number;
  lastTimestamp: number;
  fps: number;
};

const fpsCache: Record<string, FPSCacheEntry> = {};

async function computeTrackFPS(
  track: any,
  trackSid: string
): Promise<number | null> {
  if (!track?.getRTCStatsReport) return null;

  try {
    const report = await track.getRTCStatsReport();
    for (const stats of report.values()) {
      if (stats.type === 'outbound-rtp' || stats.type === 'inbound-rtp') {
        const frames = stats.framesEncoded ?? stats.framesDecoded;
        const timestamp = stats.timestamp;

        if (frames == null) continue;

        const prev = fpsCache[trackSid];
        if (prev) {
          const deltaFrames = frames - prev.lastFrames;
          const deltaTime = (timestamp - prev.lastTimestamp) / 1000; // seconds
          const fps = deltaTime > 0 ? deltaFrames / deltaTime : prev.fps;
          fpsCache[trackSid] = {
            lastFrames: frames,
            lastTimestamp: timestamp,
            fps,
          };
          return Math.round(fps);
        } else {
          fpsCache[trackSid] = {
            lastFrames: frames,
            lastTimestamp: timestamp,
            fps: 0,
          };
          return 0;
        }
      }
    }
  } catch (err) {
    console.warn('FPS stats error', err);
  }
  return null;
}
